import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Row, Col, Skeleton, Badge, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTrendUp } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchWeeklyReport } from '../../../redux/WeeklyReports';
import { useTheme } from '../../../context/ThemeContext';
import {
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummaryInfo,
  PeriodText,
  GrossSalesDisplay,
  GrossSalesAmount,
  GrossSalesLabel,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
} from './Styles';
import {
  addCommasInPrices,
  METRIC_IDS,
  METRIC_COLORS,
  METRIC_ICONS,
} from './constants';
import { WeeklyReportSummaryProps, KeyMetric } from './types';

const { Text } = Typography;

const WeeklyReportSummary: React.FC<WeeklyReportSummaryProps> = ({
  merchant,
  storeId,
  selectedDateRange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { reportData, loading } = useSelector(
    (state: RootState) => state.weeklyReports
  );

  const getDateTimestamp = (date: moment.Moment | Date | null): number | null => {
    if (!date) return null;
    if (date instanceof Date) return date.getTime();
    if (moment.isMoment(date)) return date.valueOf();
    return null;
  };

  const startTimestamp = getDateTimestamp(selectedDateRange.startDate);
  const merchantId = merchant?.id || null;

  const startDateStr = useMemo(() => {
    if (!startTimestamp) return null;
    return moment(startTimestamp).format('YYYY-MM-DD');
  }, [startTimestamp]);

  const memoizedMerchantId = useMemo(() => merchantId, [merchantId]);
  
  const previousParamsRef = useRef<string>('');
  const isInitialMountRef = useRef(true);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    if (!startDateStr || !merchant?.id || !storeId) {
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    const paramsKey = `${startDateStr}-${memoizedMerchantId || ''}-${storeId || ''}`;
    
    if (isInitialMountRef.current || previousParamsRef.current !== paramsKey) {
      isInitialMountRef.current = false;
      previousParamsRef.current = paramsKey;
      isFetchingRef.current = true;
      
      const params = {
        startDate: startDateStr,
        merchantId: memoizedMerchantId || undefined,
        storeId: storeId || undefined,
      };
      
      dispatch(fetchWeeklyReport(params)).finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [dispatch, startDateStr, memoizedMerchantId, storeId, merchant?.id]);

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  const salesSummary = reportData?.salesSummary || {
    grossSales: 0,
    netSales: 0,
    orderCount: 0,
    totalRevenue: 0,
    itemsSold: 0,
    tips: 0,
  };

  const keyMetrics: KeyMetric[] = [
    {
      id: METRIC_IDS.GROSS_SALES,
      title: 'Gross Sales',
      subTitle: 'Total sales before deductions',
      value: `$${addCommasInPrices(salesSummary.grossSales || 0)}`,
      icon: METRIC_ICONS[METRIC_IDS.GROSS_SALES],
      color: METRIC_COLORS.GROSS_SALES,
    },
    {
      id: METRIC_IDS.NET_SALES,
      title: 'Net Sales',
      subTitle: 'Sales after deductions',
      value: `$${addCommasInPrices(salesSummary.netSales || 0)}`,
      icon: METRIC_ICONS[METRIC_IDS.NET_SALES],
      color: METRIC_COLORS.NET_SALES,
    },
    {
      id: METRIC_IDS.TOTAL_REVENUE,
      title: 'Total Revenue',
      subTitle: 'Total revenue generated',
      value: `$${addCommasInPrices(salesSummary.totalRevenue || 0)}`,
      icon: METRIC_ICONS[METRIC_IDS.TOTAL_REVENUE],
      color: METRIC_COLORS.TOTAL_REVENUE,
    },
    {
      id: METRIC_IDS.TIPS,
      title: 'Tips',
      subTitle: 'Total tips received',
      value: `$${addCommasInPrices(salesSummary.tips || 0)}`,
      icon: METRIC_ICONS[METRIC_IDS.TIPS],
      color: METRIC_COLORS.TIPS,
    },
    {
      id: METRIC_IDS.ORDER_COUNT,
      title: 'Orders',
      subTitle: 'Total number of orders',
      value: (salesSummary.orderCount || 0).toString(),
      icon: METRIC_ICONS[METRIC_IDS.ORDER_COUNT],
      color: METRIC_COLORS.ORDER_COUNT,
    },
    {
      id: METRIC_IDS.ITEMS_SOLD,
      title: 'Items Sold',
      subTitle: 'Total items sold',
      value: (salesSummary.itemsSold || 0).toString(),
      icon: METRIC_ICONS[METRIC_IDS.ITEMS_SOLD],
      color: METRIC_COLORS.ITEMS_SOLD,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <SummaryCard>
        <SummaryHeader>
          <SummaryTitle>Weekly Report Summary</SummaryTitle>
          <SummaryInfo>
            {loading ? (
              <Skeleton.Input active size="small" style={{ width: 150 }} />
            ) : (
              <>
                <Text type="secondary">
                  Merchant: {reportData?.merchantName || 'N/A'}
                </Text>
                <Badge
                  color="blue"
                  text={reportData?.reportType || 'weekly'}
                />
              </>
            )}
          </SummaryInfo>
        </SummaryHeader>

        {loading ? (
          <Skeleton.Input active style={{ width: 300, marginBottom: 16 }} />
        ) : (
          reportData?.period?.startDate && reportData?.period?.endDate && (
            <PeriodText>
              Week {reportData.period.weekNumber || 'N/A'} of {reportData.period.year || 'N/A'} -{' '}
              {moment(reportData.period.startDate).format('MMM DD')} -{' '}
              {moment(reportData.period.endDate).format('MMM DD, YYYY')}
            </PeriodText>
          )
        )}

        <GrossSalesDisplay>
          {loading ? (
            <Skeleton.Input active size="large" style={{ width: 200, height: 48 }} />
          ) : (
            <GrossSalesAmount>
              ${addCommasInPrices(salesSummary.grossSales || 0)}
            </GrossSalesAmount>
          )}
          <GrossSalesLabel>
            <FontAwesomeIcon icon={faArrowTrendUp} />
            <span>Gross Sales</span>
          </GrossSalesLabel>
        </GrossSalesDisplay>

        <div>
          {loading ? (
            <Row gutter={[16, 16]}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Col>
              ))}
            </Row>
          ) : (
            <>
              <Typography.Title
                level={4}
                style={{ marginBottom: 16, fontWeight: 700 }}
              >
                Key Metrics
              </Typography.Title>
              <MetricsGrid gutter={[16, 16]}>
                {keyMetrics.map((metric) => (
                  <Col xs={24} sm={12} md={8} lg={6} xl={4} key={metric.id}>
                    <MetricCard>
                      <MetricHeader>
                        <MetricIcon color={metric.color}>
                          <FontAwesomeIcon icon={metric.icon} />
                        </MetricIcon>
                        <MetricTitle>{metric.title}</MetricTitle>
                      </MetricHeader>
                      <MetricValue>{metric.value}</MetricValue>
                    </MetricCard>
                  </Col>
                ))}
              </MetricsGrid>
            </>
          )}
        </div>
      </SummaryCard>
    </ThemeProvider>
  );
};

export default WeeklyReportSummary;
